import { createAsyncThunk } from "@reduxjs/toolkit";
import { phpClient } from "../../../network";
import {
    AttendanceTags,
    DepartmentMaster,
    GetSkipMealBreakStatusRequest,
    GetUnreadNotificationCountRequest as GetUnreadNotifCountRequest,
    GetSitesV2Request,
    FetchAttendanceTagsV2Request,
    SearchEmployeeRequest,
    IRejectWithValue,
    JobCode,
    PhpResponse,
    SkipMealBreakStatusResponseData,
    SiteType,
    TimeoffCategoryType,
    WorkflowRequest,
    ShiftMaster,
    ModuleName,
    GroupMaster,
} from "../../../types";
import { LastPunchData, LastPunchResponse } from "../../../types/state/lastpunch";
import { EmployeeDetailResponse, EmployeesApiResponse, EmployeesRequestPayload } from "../../../types/state/Employee";
import { CommonState } from "../../../types/state/common";
import { SearchEmployeeResponse, GetEmployeesResponse } from "../../../types/response";
import { isCacheValid, getCachedData, setCachedData } from "../../../utils/storageCache";

// Cache keys
const CACHE_KEYS = {
    SITES: 'cached_sites',
    DEPARTMENTS: 'cached_departments',
    JOB_CODES: 'cached_job_codes',
    ATTENDANCE_TAGS: 'cached_attendance_tags'
};

// Define state shape for thunk
type ThunkState = { common: CommonState };

export const getSites = createAsyncThunk<PhpResponse<SiteType[]>, void, IRejectWithValue>(
    "/sites", 
    async (_, { rejectWithValue }) => {
    try {
        const response = await phpClient.get<PhpResponse<SiteType[]>>({
            url: "/webclock/locations_list",
        });
        
        return response || { data: [] } as PhpResponse<SiteType[]>;
    } catch (error: any) {
        return rejectWithValue(error.message || "An error occurred");
    }
});

export const getSitesV2 = createAsyncThunk<PhpResponse<SiteType[]>, GetSitesV2Request, IRejectWithValue>(
    "/sites/v2", 
    async (data, { rejectWithValue }) => {
    try {
        const response = await phpClient.post<PhpResponse<SiteType[]>>({
            url: "/webclock/get_sites_list_v2",
            data,
        });
        
        return response || { data: [] } as PhpResponse<SiteType[]>;
    } catch (error: any) {
        return rejectWithValue(error.message || "An error occurred");
    }
});

export const getEmployeeSites = createAsyncThunk<PhpResponse<SiteType[]>, string, IRejectWithValue>(
    "common/getEmployeeSites", 
    async (empId, { rejectWithValue }) => {
    try {
        const response = await phpClient.get<PhpResponse<SiteType[]>>({
            url: `/site/getEmployeeSites/${empId}`,
        });
        
        return response || { data: [] } as PhpResponse<SiteType[]>;
    } catch (error: any) {
        return rejectWithValue(error.message || "An error occurred");
    }
});

export const getDepartments = createAsyncThunk<PhpResponse<DepartmentMaster[]>, void, IRejectWithValue>(
    "/departments", 
    async (_, { rejectWithValue }) => {
    try {
        const response = await phpClient.get<PhpResponse<DepartmentMaster[]>>({
            url: "/webclock/department_list",
        });
        
        return response;
    } catch (error: any) {
        return rejectWithValue(error.message || "An error occurred");
    }
});
export const getJobCodes = createAsyncThunk("/Jobcodes", async (undefined, { rejectWithValue }) => {
    try {
        const response = await phpClient.get<PhpResponse<JobCode[]>>({
            url: "/webclock/work_codes_list",
        });
        
        return response;
    } catch (error: any) {
        return rejectWithValue(error.message || "An error occurred");
    }
});

export const getShifts = createAsyncThunk<PhpResponse<ShiftMaster[]>, void, IRejectWithValue>(
    "/shifts",
    async (_, { rejectWithValue }) => {
        try {
            const response = await phpClient.get<PhpResponse<ShiftMaster[]>>({
                url: "/schedule/Shift",
            });
            
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const getGroups = createAsyncThunk<PhpResponse<GroupMaster[]>, void, IRejectWithValue>(
    "/groups",
    async (_, { rejectWithValue }) => {
        try {
            const response = await phpClient.get<PhpResponse<GroupMaster[]>>({
                url: "/webclock/group_list",
            });
            
            return response || { data: [] } as PhpResponse<GroupMaster[]>;
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const fetchAttendanceTags = createAsyncThunk<PhpResponse<AttendanceTags[]>, void, IRejectWithValue>(
  "attendance/fetchAttendanceTags",
  async (_, { rejectWithValue }) => {
    try {
      const response = await phpClient.post<PhpResponse<AttendanceTags[]>>({
        url: "/attendance/attendance_tags",
      });
      return response || [];
    } catch (error: any) {
      return rejectWithValue(error.message || "An error occurred while fetching attendance tags.");
    }
  }
);

export const fetchScheduleAttendanceTags = createAsyncThunk<PhpResponse<AttendanceTags[]>, void, IRejectWithValue>(
  "attendance/fetchScheduleAttendanceTags",
  async (_, { rejectWithValue }) => {
    try {
      // Fetch attendance tags for scheduling tool with tag_type "2" (Schedule only)
      const response = await phpClient.post<PhpResponse<AttendanceTags[]>>({
        url: "/attendance/attendance_tags",
        data: {
          tag_type: "2"
        },
      });
      
      return response || [];
    } catch (error: any) {
      return rejectWithValue(error.message || "An error occurred while fetching schedule attendance tags.");
    }
  }
);

export const fetchAttendanceTagsV2 = createAsyncThunk<PhpResponse<AttendanceTags[]>, FetchAttendanceTagsV2Request, IRejectWithValue>(
  "attendance/fetchAttendanceTagsV2",
  async (data, { rejectWithValue }) => {
    try {
      const response = await phpClient.post<PhpResponse<AttendanceTags[]>>({
        url: "/attendance/get_attendance_tags_v2",
        data,
      });
      return response || { data: [] } as PhpResponse<AttendanceTags[]>;
    } catch (error: any) {
      return rejectWithValue(error.message || "An error occurred while fetching attendance tags.");
    }
  }
);

export const getAllTimeoffCategories = createAsyncThunk<TimeoffCategoryType[], void, IRejectWithValue>(
    "timeoff/getTimeoffCategoryList",
    async (data, { rejectWithValue }) => {
        try {
            const response = await phpClient.get<PhpResponse<TimeoffCategoryType[]>>({
                url: "/timeoff/timeoff_category_list",
            });

            return response.data || [];
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const getAssignedTimeoffCategories = createAsyncThunk<TimeoffCategoryType[], { employee_id?: string } | void, IRejectWithValue>(
    "timeoff/getAssginedTimeoffCategoryList",
    async (data, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<TimeoffCategoryType[]>>({
                url: "/timeoff/list_assigned_category",
                data: data || {},
            });

            return response.data || [];
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);



export const callPhpWorkflow = createAsyncThunk<{ header_id: string } | undefined, WorkflowRequest, IRejectWithValue>(
    "php/workflow",
    async (data, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<{ header_id: string }>>({
                // url: "/workflow/create",
                url: "/workflow3/process_step",
                data,
            });

            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const skipMealBreak = createAsyncThunk<
  PhpResponse<unknown>,
  {
    workflow_code: string;
    entry_id: number;
    current_datetime: string;
    notes: string;
  },
  { rejectValue: string | undefined }
>(
  "common/skipMealBreak",
  async (data, { rejectWithValue }) => {
    try {
      const response = await phpClient.post<PhpResponse<any>>({
        url: "/webclock/skip_meal_break",
        data,
      });

      return response?.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message);
    }
  }
);

export const fetchSkipMealBreakStatus = createAsyncThunk<
  PhpResponse<SkipMealBreakStatusResponseData>,
  GetSkipMealBreakStatusRequest,
  IRejectWithValue
>(
  "common/fetchSkipMealBreakStatus",
  async (data, { rejectWithValue }) => {
    try {
      const response = await phpClient.post<PhpResponse<SkipMealBreakStatusResponseData>>({
        url: "/attendance/skip_meal_break_status",
        data,
      });

      return response || { data: undefined } as PhpResponse<SkipMealBreakStatusResponseData>;
    } catch (error: any) {
      return rejectWithValue(error.message || "An error occurred");
    }
  }
);

export const getUnreadNotifCount = createAsyncThunk<number | undefined, GetUnreadNotifCountRequest, IRejectWithValue>(
    "common/getUnreadMessageCount",
    async (data, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<number>>({
                url: "/notification/notification_unread_count",
                data
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const getLastPunch = createAsyncThunk("common/lastpunch", async (undefined, thunkAPI) => {
    const { rejectWithValue } = thunkAPI;

    try {
        const response = await phpClient.get<PhpResponse<SiteType[]>>({
            url: "webclock/lastpunch",
        });
        return response;
    } catch (error) {
        return rejectWithValue(error);
    }
});


// Async thunk to fetch the last punch
export const fetchLastPunch = createAsyncThunk<
LastPunchData,
  string, 
  { rejectValue: string }
>(
  "lastPunch/fetchLastPunch",
  async (datetime: string, { rejectWithValue }) => {
    try {
      const response = await phpClient.get<PhpResponse<LastPunchData>>({
        url: `/webclock/lastpunch?datetime=${datetime}`,
      });
      if (response.data) {
        return response.data;
      } else {
        throw new Error("Invalid response data");
      }
    } catch (error: any) {
      return rejectWithValue(error.message || "Something went wrong");
    }
  }
);


export const fetchEmployees = createAsyncThunk<
  EmployeesApiResponse, // Return type of the payload creator (resolved value)
  EmployeesRequestPayload, // Argument type (request payload)
  {
    rejectValue: { error: string };
  }
>(
  "employees/fetchEmployees",
  async (payload, thunkAPI) => {
    try {
      const requestPayload = {
        ...payload,
        module_name: payload.module_name ?? "",
      };
      const response = await phpClient.post<EmployeesApiResponse>({
        url: "/employees",
        data: requestPayload,
      });
      return response ?? [];
    } catch (error: any) {
      console.error("*** fetchEmployees API error", error);
      return thunkAPI.rejectWithValue({ error: error.message || "Failed to fetch employees" });
    }
  }
);

export const searchEmployee = createAsyncThunk<SearchEmployeeResponse, SearchEmployeeRequest, IRejectWithValue>(
  "common/searchEmployee",
  async (data, { rejectWithValue }) => {
    let payload:any = { 
      search: data.search,
      departments: data.departments ?? "",
      ...(data.priv ? { priv: data.priv } : { module_name: data.module_name ?? "" })
    };

    if(data.emp_status) {
      payload = {
        ...payload,
        emp_status: data.emp_status
      };
    }
    try {
      const response = await phpClient.post<SearchEmployeeResponse>({
        url: "/employees/employee_search",
        data: payload,
      });
      
      return response || { status: false, data: { total_entries: "0", pages: 0, employees: [] }, message: "No results found" };
    } catch (error: any) {
      return rejectWithValue(error.message || "An error occurred while searching employees");
    }
  }
);

export const getEmployees = createAsyncThunk<GetEmployeesResponse, { co_worker?: boolean; l_i_m_i_t?: string; module_name?: string } | void, IRejectWithValue>(
  "common/getEmployees",
  async (params, { rejectWithValue }) => {
    try {
      const response = await phpClient.post<GetEmployeesResponse>({
        url: "/employees",
        data: {
          extraRequestFields: ["department_detail"],
          co_worker: params?.co_worker === true,
          l_i_m_i_t: params?.l_i_m_i_t || "50",
          module_name: params?.module_name ?? ""
        },
      });
      
      return response || { status: false, data: [], message: "No employees found", meta: { count: 0, page: 1, limit: 50 } };
    } catch (error: any) {
      return rejectWithValue(error.message || "An error occurred while fetching employees");
    }
  }
);

// Flex Field Value Types
export interface FlexFieldValueData {
  custom_field_code: string;
  custom_field_name: string;
  custom_field_type: string;
  field_value: string;
  field_value_json: any;
  value_effective_date: string;
}

export interface FlexFieldValueResponse {
  status: boolean;
  data: FlexFieldValueData | null;
  message?: string;
}

export interface GetFlexFieldValueRequest {
  field_code: string;
  employee_id: string;
}

// Get Flex Field Value for Employee - Common API for both Remote Work and Paris Work
export const getFlexFieldValue = createAsyncThunk<
  { fieldCode: string; data: FlexFieldValueData },
  GetFlexFieldValueRequest,
  IRejectWithValue
>(
  "common/getFlexFieldValue",
  async (request, { rejectWithValue }) => {
    try {
      const response = await phpClient.post<FlexFieldValueResponse>({
        url: "/employees/get_flex_field_value_employee",
        data: {
          field_code: request.field_code,
          employee_id: request.employee_id,
        },
      });

      if (!response.status || !response.data) {
        return rejectWithValue(response.message || "Failed to fetch flex field value");
      }

      return {
        fieldCode: request.field_code,
        data: response.data,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.message || 
        "An error occurred while fetching flex field value"
      );
    }
  }
);
